import {
  QuicksightDashboard,
  QuicksightService,
} from "../../../../services/QuicksightService";
import {syncQuicksightDashboardsReportingPermission} from "../quicksight-dashboards-macro-checkbox/syncQuicksightDashboardsReportingPermission";
import {PermissionsByUserId} from "./bulkPermissions.helpers";

export type DashboardIdsByEmail = Readonly<
  Record<string, ReadonlyArray<string>>
>;

export type DashboardUserAssignment = Readonly<{
  dashboardId: string;
  email: string;
}>;

export const buildUserDashboardsCacheValue = (
  dashboardIds: ReadonlyArray<string>,
  dashboardOptions: ReadonlyArray<{id?: string; name?: string}>,
  previousDashboards: ReadonlyArray<QuicksightDashboard> = [],
): ReadonlyArray<QuicksightDashboard> => {
  const nextIdSet = new Set(dashboardIds);
  const kept = previousDashboards.filter(dashboard => nextIdSet.has(dashboard.id));
  const keptIds = new Set(kept.map(dashboard => dashboard.id));
  const optionsById = new Map(
    dashboardOptions
      .filter(
        (dashboard): dashboard is {id: string; name?: string} =>
          Boolean(dashboard.id),
      )
      .map(dashboard => [dashboard.id, dashboard]),
  );

  const added = dashboardIds
    .filter(id => !keptIds.has(id))
    .map(id => ({
      id,
      name: optionsById.get(id)?.name ?? id,
    }));

  return [...kept, ...added];
};

export const applyBulkDashboardToggle = (
  dashboardsByEmail: DashboardIdsByEmail,
  emails: ReadonlyArray<string>,
  dashboardId: string,
  grantToAll: boolean,
): DashboardIdsByEmail => {
  const nextDashboardsByEmail = {...dashboardsByEmail};

  emails.forEach(email => {
    const currentIds = [...(nextDashboardsByEmail[email] ?? [])];

    if (grantToAll) {
      if (!currentIds.includes(dashboardId)) {
        currentIds.push(dashboardId);
      }
    } else {
      const dashboardIndex = currentIds.indexOf(dashboardId);

      if (dashboardIndex >= 0) {
        currentIds.splice(dashboardIndex, 1);
      }
    }

    nextDashboardsByEmail[email] = currentIds;
  });

  return nextDashboardsByEmail;
};

export const applyBulkDashboardCategoryToggle = (
  dashboardsByEmail: DashboardIdsByEmail,
  emails: ReadonlyArray<string>,
  dashboardIds: ReadonlyArray<string>,
  grantToAll: boolean,
): DashboardIdsByEmail =>
  dashboardIds.reduce(
    (nextDashboardsByEmail, dashboardId) =>
      applyBulkDashboardToggle(
        nextDashboardsByEmail,
        emails,
        dashboardId,
        grantToAll,
      ),
    dashboardsByEmail,
  );

export const isBulkDashboardPending = (
  savedDashboardsByEmail: DashboardIdsByEmail,
  draftDashboardsByEmail: DashboardIdsByEmail,
  emails: ReadonlyArray<string>,
  dashboardId: string,
): boolean =>
  emails.some(email => {
    const saved = savedDashboardsByEmail[email] ?? [];
    const draft = draftDashboardsByEmail[email] ?? [];

    return saved.includes(dashboardId) !== draft.includes(dashboardId);
  });

export const countBulkPendingDashboardChanges = (
  savedDashboardsByEmail: DashboardIdsByEmail,
  draftDashboardsByEmail: DashboardIdsByEmail,
  emails: ReadonlyArray<string>,
): number => {
  const dashboardIds = new Set<string>();

  emails.forEach(email => {
    (savedDashboardsByEmail[email] ?? []).forEach(id => dashboardIds.add(id));
    (draftDashboardsByEmail[email] ?? []).forEach(id => dashboardIds.add(id));
  });

  let changeCount = 0;

  dashboardIds.forEach(dashboardId => {
    if (
      isBulkDashboardPending(
        savedDashboardsByEmail,
        draftDashboardsByEmail,
        emails,
        dashboardId,
      )
    ) {
      changeCount += 1;
    }
  });

  return changeCount;
};

/** Revoke every dashboard assigned to the given users (QuickSight admin `users` shape). */
export const buildRevokeAllDashboardAssignments = (
  users: ReadonlyArray<{
    email?: string;
    dashboards?: ReadonlyArray<{id: string}>;
  }>,
): ReadonlyArray<DashboardUserAssignment> => {
  const assignments: DashboardUserAssignment[] = [];

  users.forEach(user => {
    if (!user.email) {
      return;
    }

    (user.dashboards ?? []).forEach(dashboard => {
      assignments.push({dashboardId: dashboard.id, email: user.email});
    });
  });

  return assignments;
};

/**
 * Loads dashboard memberships from QuickSight admin API, then builds revoke payloads.
 * Permissions list/detail DTOs only include processed memberships from reside-app.
 */
export const fetchRevokeAllDashboardAssignments = async (
  emails: ReadonlyArray<string>,
): Promise<ReadonlyArray<DashboardUserAssignment>> => {
  if (emails.length === 0) {
    return [];
  }

  const {data} = await QuicksightService.getUsersDashboards(emails);

  return buildRevokeAllDashboardAssignments(data.users);
};

export const diffBulkDashboardChanges = (
  savedDashboardsByEmail: DashboardIdsByEmail,
  draftDashboardsByEmail: DashboardIdsByEmail,
  emails: ReadonlyArray<string>,
): {
  granted: ReadonlyArray<DashboardUserAssignment>;
  revoked: ReadonlyArray<DashboardUserAssignment>;
} => {
  const granted: DashboardUserAssignment[] = [];
  const revoked: DashboardUserAssignment[] = [];

  emails.forEach(email => {
    const saved = new Set(savedDashboardsByEmail[email] ?? []);
    const draft = new Set(draftDashboardsByEmail[email] ?? []);

    draft.forEach(dashboardId => {
      if (!saved.has(dashboardId)) {
        granted.push({dashboardId, email});
      }
    });

    saved.forEach(dashboardId => {
      if (!draft.has(dashboardId)) {
        revoked.push({dashboardId, email});
      }
    });
  });

  return {granted, revoked};
};

export const applyQuicksightReportingSync = (
  permissionsByUserId: PermissionsByUserId,
  users: ReadonlyArray<{id?: string; email?: string}>,
  dashboardsByEmail: DashboardIdsByEmail,
): PermissionsByUserId => {
  let hasChanges = false;
  const nextPermissionsByUserId = {...permissionsByUserId};

  users.forEach(user => {
    if (!user.id) {
      return;
    }

    const hasDashboardAccess = user.email
      ? (dashboardsByEmail[user.email] ?? []).length > 0
      : false;
    const currentPermissions = nextPermissionsByUserId[user.id] ?? [];
    const syncedPermissions = syncQuicksightDashboardsReportingPermission(
      hasDashboardAccess,
      currentPermissions,
    );

    if (syncedPermissions !== currentPermissions) {
      hasChanges = true;
      nextPermissionsByUserId[user.id] = [...syncedPermissions];
    }
  });

  return hasChanges ? nextPermissionsByUserId : permissionsByUserId;
};
